'use client'

/**
 * ChartStatsReadout — the one OHLC / volume / $vol / RVOL row, shared by every chart on the site.
 *
 * Owner 2026-07-28: "show all the price info ... so i don't have to scroll down", then "make the feed
 * chart more in line with the chart page". Extracted rather than copied so the two cannot drift: the
 * chart page and the /stocks/[symbol] charts render the SAME markup from the SAME props, and the next
 * fix lands in both.
 *
 * PRESENTATIONAL ONLY. It takes a bar and renders it. Each chart owns its own hover detection, because
 * they are three different technologies (lightweight-charts crosshair, hand-rolled SVG mousemove, and
 * a canvas) and sharing that part would mean inventing an abstraction over all three for no gain.
 *
 * `hovering` distinguishes "the bar under your cursor" from "the most recent bar", which is why the
 * caller passes it rather than us inferring it from a null check: a chart with no hover support still
 * wants the latest bar shown, and it should not be labelled AT CURSOR.
 */

export type ReadoutBar = {
  /** ms epoch */
  t: number
  open: number
  high: number
  low: number
  close: number
  volume: number
}

export type ReadoutRvol = { v: number; thin: boolean } | null

const compact = (n: number) =>
  n >= 1e9 ? `${(n / 1e9).toFixed(1)}B`
  : n >= 1e6 ? `${(n / 1e6).toFixed(1)}M`
  : n >= 1e3 ? `${(n / 1e3).toFixed(1)}K`
  : `${Math.round(n)}`

/** Sub-dollar names need more precision or every cell reads $0.00. */
const px = (n: number) => (n < 1 ? n.toFixed(4) : n.toFixed(2))

function Cell({ k, v, tone }: { k: string; v: string; tone?: string }) {
  return (
    <span className="flex items-baseline gap-1">
      <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-600">{k}</span>
      <span className={`text-[11px] font-medium tabular-nums ${tone ?? 'text-foreground/80'}`}>{v}</span>
    </span>
  )
}

/** Stand-in for `reserve`: a bar whose cells print at the character counts a typical bar does (a two-digit
 *  day and a 12:00 PM time, three-digit prices, a 10.0M volume). The row is font-mono, so equal character
 *  counts are equal widths, and the stand-in wraps onto the same number of lines as the real row will. */
const RESERVE_BAR: ReadoutBar = { t: Date.UTC(2026, 10, 14, 17, 0), open: 100, high: 100, low: 100, close: 100, volume: 10_000_000 }

type ReadoutProps = {
  bar: ReadoutBar | null
  rvol?: ReadoutRvol
  /** Live 1-minute and 5-minute relative volume, computed from the raw 1-minute bars and INDEPENDENT of
   *  the displayed timeframe. When provided and the user is NOT hovering a specific candle, both are shown
   *  side by side in place of the single RVOL cell, so the immediate burst and the sustained 5-minute read
   *  are visible at once without switching timeframes. Customer request (support thread "RVOL - 5min"). */
  rvol1m?: ReadoutRvol
  rvol5m?: ReadoutRvol
  hovering: boolean
  /** 'bar' = a row above the chart · 'overlay' = floats inside the pane (feed charts). */
  variant?: 'bar' | 'overlay'
  className?: string
  /** A DAILY bar covers a whole session, so it has no time-of-day. Defaults false: this component is
   *  shared with the /stocks/[symbol] charts and every existing caller is intraday. */
  daily?: boolean
  /** With no bar yet, hold the row's space instead of rendering nothing (register
   *  chart-volume-card-shifts-after-mount-1104). On /chart the row appeared ~2s after mount, 55px tall, and
   *  pushed the legend, the chart and the Volume card down: v 0.0638 of the 0.0945 a mounted chart measured.
   *  The frame stays visible (an empty bordered row reads as loading) and its cells are an invisible
   *  stand-in. Opt-in and 'bar' only, so the /stocks/[symbol] overlay charts are unchanged. */
  reserve?: boolean
}

function ReadoutCells({ bar, rvol, rvol1m, rvol5m, hovering, variant, daily }: Omit<ReadoutProps, 'bar' | 'className' | 'reserve'> & { bar: ReadoutBar }) {
  const up = bar.close >= bar.open
  const chg = bar.open > 0 ? ((bar.close - bar.open) / bar.open) * 100 : null
  const tone = up ? 'text-emerald-700 dark:text-emerald-300' : 'text-rose-600 dark:text-rose-300'
  // A daily bar is stamped at MIDNIGHT ET of its session, so rendering a time-of-day invented a
  // "8:00 PM" that means nothing and read as if the bar were an evening print. Date only, and no " ET"
  // suffix, because a zone label on a bare date is noise.
  const when = daily
    ? new Date(bar.t).toLocaleDateString('en-US', {
        timeZone: 'America/New_York', month: 'short', day: 'numeric', year: 'numeric',
      })
    : new Date(bar.t).toLocaleString('en-US', {
        timeZone: 'America/New_York', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true,
      })

  return (
    <>
      <span className={`text-[11px] font-bold ${hovering ? 'text-amber-600 dark:text-amber-300' : 'text-muted-foreground'}`}>
        {hovering ? 'AT CURSOR' : 'LATEST'}
      </span>
      <span className="text-[10px] text-zinc-600">{when}{daily ? '' : ' ET'}</span>
      {variant === 'bar' && <span className="h-3 w-px bg-border" />}
      <Cell k="O" v={`$${px(bar.open)}`} />
      <Cell k="H" v={`$${px(bar.high)}`} />
      <Cell k="L" v={`$${px(bar.low)}`} />
      {/* The newest bar is still forming, so its close is the live price, not a settled close. */}
      <Cell k={hovering ? 'C' : 'NOW'} v={`$${px(bar.close)}`} tone={tone} />
      {chg != null && <Cell k="CHG" v={`${up ? '+' : ''}${chg.toFixed(2)}%`} tone={tone} />}
      {variant === 'bar' && <span className="h-3 w-px bg-border" />}
      <Cell k="VOL" v={compact(bar.volume ?? 0)} />
      <Cell k="$VOL" v={`$${compact((bar.volume ?? 0) * bar.close)}`} />
      {/* RVOL is optional: a chart whose API cannot supply a baseline simply omits the cell rather
          than rendering a misleading "--" that looks like a value of zero.
          · NOT hovering + the live 1m/5m pair supplied  → show BOTH side by side (the customer ask).
          · Hovering a specific candle, or a caller that passes only `rvol` → the single RVOL cell. */}
      {(() => {
        const rvolCell = (k: string, r: ReadoutRvol) => (
          <Cell
            key={k}
            k={k}
            v={r ? `${r.v >= 10 ? Math.round(r.v) : r.v.toFixed(1)}x${r.thin ? ' (thin)' : ''}` : '--'}
            tone={!r ? 'text-muted-foreground' : r.v >= 3 ? 'text-rose-600 dark:text-rose-300' : r.v >= 1 ? 'text-amber-600 dark:text-amber-300' : 'text-muted-foreground'}
          />
        )
        if (!hovering && (rvol1m !== undefined || rvol5m !== undefined)) {
          return <>{rvolCell('RVOL 1m', rvol1m ?? null)}{rvolCell('RVOL 5m', rvol5m ?? null)}</>
        }
        return rvol !== undefined ? rvolCell('RVOL', rvol ?? null) : null
      })()}
    </>
  )
}

export default function ChartStatsReadout({
  bar, rvol, rvol1m, rvol5m, hovering, variant = 'bar', className = '', daily = false, reserve = false,
}: ReadoutProps) {
  const wrap = variant === 'overlay'
    ? `pointer-events-none absolute left-1.5 top-1.5 z-10 flex flex-wrap items-center gap-x-2.5 gap-y-0.5 rounded-lg bg-background/80 px-2 py-1 backdrop-blur-sm ${className}`
    : `flex flex-wrap items-center gap-x-3 gap-y-1 rounded-xl border border-border bg-card/40 px-3 py-2 ${className}`

  if (!bar) {
    if (!reserve || variant !== 'bar') return null
    // display:contents keeps the stand-in cells as direct flex items of the row, so they wrap exactly
    // as real cells would; visibility is inherited, so none of them paint.
    return (
      <div aria-hidden data-readout-reserve className={`${wrap} font-mono`}>
        <span className="invisible contents">
          <ReadoutCells bar={RESERVE_BAR} rvol={rvol} rvol1m={rvol1m} rvol5m={rvol5m} hovering={false} variant={variant} daily={daily} />
        </span>
      </div>
    )
  }

  return (
    <div className={`${wrap} font-mono`}>
      <ReadoutCells bar={bar} rvol={rvol} rvol1m={rvol1m} rvol5m={rvol5m} hovering={hovering} variant={variant} daily={daily} />
    </div>
  )
}
